import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { NavBar } from '../components/NavBar';
import { BackToHome } from '../components/BackToHome';
import { useI18n } from '../i18n/I18nContext';
import { useLocalizedContent } from '../i18n/useLocalizedContent';
import { SocialIconsRow } from '../components/SocialIconsRow';
import { Tx } from '../components/EditMode';
import { Calendar, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JourneyPage = () => {
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useI18n();
  const localized = useLocalizedContent();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [postsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/journey`),
        axios.get(`${API}/settings/public`),
      ]);
      setPosts(postsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString(lang === 'no' ? 'nb-NO' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  // Same family image as home — fall back to a per-journey override if admin sets one.
  const heroImage = settings?.journey_hero_image_url || settings?.profile_image || '';
  const heroImageUrl = heroImage
    ? (heroImage.startsWith('http') ? heroImage : `${process.env.REACT_APP_BACKEND_URL}${heroImage}`)
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <NavBar />
      <BackToHome />

      {/* ===== Cinematic Hero with family photo ===== */}
      <div data-testid="journey-hero" className="relative w-full h-[55vh] min-h-[360px] max-h-[520px] overflow-hidden">
        {heroImageUrl ? (
          <img
            data-testid="journey-hero-image"
            src={heroImageUrl}
            alt={settings?.mission_name || 'Family photo'}
            className="absolute inset-0 w-full h-full object-cover scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-sky-900 via-blue-900 to-slate-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/55 to-slate-900/90"></div>

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] tracking-tight drop-shadow-2xl">
            <Tx k="journey.title" />
          </h1>
          <p className="text-base sm:text-lg text-white/90 font-['Manrope'] leading-relaxed max-w-2xl mx-auto drop-shadow">
            <Tx k="journey.subtitle" />
          </p>
          <div className="mt-5">
            <SocialIconsRow size="md" />
          </div>
        </div>
      </div>

      {/* ===== Posts ===== */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : posts.length === 0 ? (
          <div data-testid="no-posts-message" className="text-center py-12">
            <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-['Manrope']">
              <Tx k="journey.empty" />
            </p>
          </div>
        ) : (
          <div data-testid="journey-posts-list" className="space-y-8">
            {posts.map((post, index) => (
              <article
                key={post.id}
                data-testid={`journey-post-${index}`}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:-translate-y-1 transition-transform duration-200"
                style={{ animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both` }}
              >
                {post.image_url && (
                  <img
                    data-testid={`journey-post-image-${index}`}
                    src={`${process.env.REACT_APP_BACKEND_URL}${post.image_url}`}
                    alt={post.title}
                    className="w-full h-64 object-cover"
                  />
                )}
                <div className="p-8">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                    <Calendar className="w-4 h-4" />
                    <time dateTime={post.created_at}>{formatDate(post.created_at)}</time>
                  </div>
                  <h3 data-testid={`journey-post-title-${index}`} className="text-2xl font-bold text-slate-900 mb-4 font-['Outfit']">
                    {localized(post, 'title')}
                  </h3>
                  <div data-testid={`journey-post-content-${index}`} className="text-slate-600 font-['Manrope'] leading-relaxed whitespace-pre-wrap">
                    {localized(post, 'content')}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
